import { apiBaseUrl, apiGet, apiPost, ApiRequestError } from "@/services/api/httpClient";
import type {
  ContributionDetail,
  ContributionSummary,
  MapPreviewPoint,
  PointCorrectionBatchPayload,
  PointCorrectionBatchResponse,
  RecentContribution,
  SubmitContributionPayload
} from "@/types/contribution";

interface ApiEnvelope<T> {
  data: T;
  requestId: string;
}

interface ApiErrorEnvelope {
  error?: {
    code?: string;
    message?: string;
  };
}

export function fetchFeaturedContributions(
  limit = 4,
  signal?: AbortSignal
): Promise<ContributionSummary[]> {
  return apiGet<ContributionSummary[]>(`/contributions/featured?limit=${limit}`, signal);
}

export function fetchRecentContributions(
  limit = 5,
  signal?: AbortSignal
): Promise<RecentContribution[]> {
  return apiGet<RecentContribution[]>(`/contributions/recent?limit=${limit}`, signal);
}

export function fetchContributionMapPreview(
  signal?: AbortSignal
): Promise<MapPreviewPoint[]> {
  return apiGet<MapPreviewPoint[]>("/contributions/map-preview", signal);
}

export interface ContributionMapPoint {
  id: string;
  sourceKind?: string;
  detailId?: string;
  type: string;
  typeLabel: string;
  title: string;
  credibilityLevel: string;
  longitude: number | null;
  latitude: number | null;
  submittedAt: string;
  buildingId?: string | null;
  buildingName?: string | null;
  publishStatus?: string | null;
}

export function fetchContributionMapPoints(
  period: "all" | "week" | "month" = "all",
  signal?: AbortSignal
): Promise<ContributionMapPoint[]> {
  return apiGet<ContributionMapPoint[]>(`/contributions/map-points?period=${period}`, signal);
}

export interface ContributionLikeResult {
  liked: boolean;
  likeCount: number;
}

export function toggleContributionLike(
  submissionId: string,
  signal?: AbortSignal
): Promise<ContributionLikeResult> {
  return apiPost<ContributionLikeResult>(
    `/contributions/${encodeURIComponent(submissionId)}/like`,
    undefined,
    signal
  );
}

export type ContributionBrowsePeriod = "all" | "week" | "month";

export type ContributionBrowseType =
  | "all"
  | "old_photo"
  | "current_photo"
  | "story"
  | "correction"
  | "building_info";

export interface ContributionBrowseItem {
  id: string;
  type: string;
  typeLabel: string;
  title: string;
  excerpt: string;
  locationName: string | null;
  reviewStatus: string;
  reviewStatusLabel: string;
  credibilityLevel: string;
  authorName: string;
  imageUrl: string | null;
  likeCount?: number;
  submittedAt: string;
  longitude: number | null;
  latitude: number | null;
}

export interface ContributionBrowsePage {
  items: ContributionBrowseItem[];
  total: number;
  page: number;
  size: number;
}

export interface ContributionBrowseParams {
  period?: ContributionBrowsePeriod;
  type?: ContributionBrowseType;
  page?: number;
  size?: number;
}

export interface ContributionLeaderboardEntry {
  userId: string;
  displayName: string;
  avatarUrl: string | null;
  submissionCount: number;
  photoCount: number;
  favoriteCount: number;
  visitCount: number;
  checkinCount: number;
  score: number;
}

export function browseContributions(
  params: ContributionBrowseParams = {},
  signal?: AbortSignal
): Promise<ContributionBrowsePage> {
  const search = new URLSearchParams();
  search.set("period", params.period ?? "all");
  search.set("type", params.type ?? "all");
  search.set("page", String(params.page ?? 1));
  search.set("size", String(params.size ?? 12));
  return apiGet<ContributionBrowsePage>(
    `/contributions/browse?${search.toString()}`,
    signal
  );
}

export function fetchContributionLeaderboard(
  limit = 10,
  signal?: AbortSignal
): Promise<ContributionLeaderboardEntry[]> {
  return apiGet<ContributionLeaderboardEntry[]>(
    `/contributions/leaderboard?limit=${limit}`,
    signal
  );
}

export function fetchMyContributions(signal?: AbortSignal): Promise<ContributionSummary[]> {
  return apiGet<ContributionSummary[]>("/contributions/mine", signal);
}

export function fetchContributionDetail(
  id: string,
  signal?: AbortSignal
): Promise<ContributionDetail> {
  return apiGet<ContributionDetail>(`/contributions/${encodeURIComponent(id)}`, signal);
}

export async function submitContribution(
  payload: SubmitContributionPayload,
  signal?: AbortSignal
): Promise<ContributionDetail> {
  const form = new FormData();
  appendText(form, "type", payload.type);
  appendText(form, "title", payload.title);
  appendText(form, "content", payload.content);
  appendText(form, "locationName", payload.locationName);
  appendNumber(form, "longitude", payload.longitude);
  appendNumber(form, "latitude", payload.latitude);
  appendText(form, "eraText", payload.eraText);
  appendText(form, "sourceNote", payload.sourceNote);
  appendText(form, "authorName", payload.authorName);
  appendText(form, "contact", payload.contact);
  appendText(form, "buildingId", payload.buildingId);
  appendText(form, "consentToPublish", String(payload.consentToPublish));
  payload.images.forEach((file) => form.append("images", file));

  let response: Response;
  try {
    response = await fetch(`${apiBaseUrl}/contributions`, {
      method: "POST",
      credentials: "include",
      body: form,
      signal
    });
  } catch {
    throw new ApiRequestError(
      "无法连接共建服务，请确认后端 8080 正在运行；如果正在上传照片，请压缩到单张 20MB 以内后重试。",
      0,
      "NETWORK_ERROR"
    );
  }
  await assertOk(response);
  const envelope = (await response.json()) as ApiEnvelope<ContributionDetail>;
  return envelope.data;
}

export function submitPointCorrections(
  payload: PointCorrectionBatchPayload,
  signal?: AbortSignal
): Promise<PointCorrectionBatchResponse> {
  return apiPost<PointCorrectionBatchResponse>(
    "/contributions/point-corrections",
    payload,
    signal
  );
}

function appendText(form: FormData, key: string, value: string | undefined): void {
  if (value !== undefined && value.trim() !== "") {
    form.append(key, value.trim());
  }
}

function appendNumber(form: FormData, key: string, value: number | undefined): void {
  if (value !== undefined && Number.isFinite(value)) {
    form.append(key, String(value));
  }
}

async function assertOk(response: Response): Promise<void> {
  if (response.ok) return;
  let message = `共建服务请求失败（${response.status}）`;
  let code = "REQUEST_FAILED";
  try {
    const payload = (await response.json()) as ApiErrorEnvelope;
    message = payload.error?.message || message;
    code = payload.error?.code || code;
  } catch {
    // Keep the generic HTTP message when the server did not return JSON.
  }
  throw new ApiRequestError(message, response.status, code);
}
